import "./new-employee-payment.scss";

import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";
import { Button } from "primereact/button";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { useQyCreateEmployeePayment } from "@core/query/employee-payment.query";
import {
  EmployeePaymentFormRule,
  EmployeePaymentFormSchema,
} from "@core/form/form.rule";
import { employeePaymentFormDefault } from "@core/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import HeaderContent from "@shared/ui/header-content/header-content";
import FormEmployeePayment from "../form-employee-payment/form-employee-payment";
import { useFormDropdownPersonContext } from "@domain/person/form-dropdown-person/form-dropdown-person.context";

export function NewEmployeePayment() {
  // LOCAL DECLARATION
  const { selectedPersonData } = useFormDropdownPersonContext();
  const navigate = useNavigate();
  const { mapCreateEmployeePayment } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New EmployeePayment is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addEmployeePayment, isLoading: isCreating } =
    useQyCreateEmployeePayment(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<EmployeePaymentFormSchema>({
    defaultValues: employeePaymentFormDefault,
    resolver: zodResolver(EmployeePaymentFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: EmployeePaymentFormSchema) => {
    const updatedForm = {
      ...form,
      bucketCode: selectedPersonData?.bucket.code,
      userCode: selectedPersonData?.code,
    } as EmployeePaymentFormSchema;
    const formData = mapCreateEmployeePayment(updatedForm);
    addEmployeePayment(formData);
  };
  const handleValidateError = (err: FieldErrors<EmployeePaymentFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return (
    <div id="EmployeePayment" className="employee-payment">
      <HeaderContent
        title="New Employee Payment"
        backIcon={<FaChevronLeft />}
        onBack={() => navigate("../")}
      >
        <Button
          className="w-full block"
          label="Save"
          disabled={isCreating}
          onClick={handleSubmit(handleValidate, handleValidateError)}
        ></Button>
      </HeaderContent>

      <div className="p-7">
        <FormProvider {...formMethod}>
          <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 bg-white">
            <FormEmployeePayment />
          </section>
        </FormProvider>
      </div>
    </div>
  );
}

export default NewEmployeePayment;
